import { useEffect, useRef, useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { uploadPhoto, type PhotoPurpose } from '../../lib/upload'
import { pushToast } from '../../lib/toast'

/**
 * The composer.
 *
 * Enter sends, Shift+Enter breaks a line. The draft lives in the store so it
 * survives a view switch — losing a half-typed thought to a stray tap is a
 * small betrayal that people remember.
 *
 * Photos come along too: of her work, or of the filled-in paper sheets.
 * They upload on send, and the message carries them so Sapling can look.
 */
type Pending = { id: string; file: File; url: string }

export function Composer({ onSend, childId, draft, placeholder }: {
  onSend: (text: string, artifactIds?: string[]) => void
  childId: string
  /** A draft of its own (a game's page), instead of the thread's shared one. */
  draft?: [string, (text: string) => void]
  placeholder?: string
}) {
  const shared = useAuroraStore((s: any) => s.ui.composing)
  const text = draft ? draft[0] : shared
  const online = useAuroraStore((s: any) => s.ui.online)
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [pending, setPending] = useState<Pending[]>([])
  const [purpose, setPurpose] = useState<PhotoPurpose>('work')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [text])

  useEffect(() => () => pending.forEach((p) => URL.revokeObjectURL(p.url)), [pending])

  const set = draft ? draft[1] : (v: string) => (auroraStore.getState() as any).setComposing(v)

  const addFiles = (files: FileList | File[] | null) => {
    if (!files) return
    const next = [...files].filter((f) => f.type.startsWith('image/')).slice(0, 8 - pending.length)
    if (!next.length) return
    setPending((p) => [...p, ...next.map((file) => ({ id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`, file, url: URL.createObjectURL(file) }))])
  }

  const submit = async () => {
    const trimmed = text.trim()
    if ((!trimmed && !pending.length) || busy) return
    if (!pending.length) { onSend(trimmed); if (draft) set(''); return }
    if (!online) { pushToast('Photos need a connection to upload. Try again once you are back online.'); return }
    setBusy(true)
    try {
      const ids: string[] = []
      for (const p of pending) ids.push(await uploadPhoto(p.file, { childId, purpose }))
      const fallback = purpose === 'sheet'
        ? `Here ${pending.length === 1 ? 'is a photo' : `are ${pending.length} photos`} of the filled-in sheets. Please read them into the record.`
        : `Here ${pending.length === 1 ? 'is a photo' : `are ${pending.length} photos`} of her work.`
      onSend(trimmed || fallback, ids)
      if (draft) set('')
      setPending([])
    } catch (err) {
      pushToast(`Could not upload: ${err instanceof Error ? err.message : 'try again'}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="composer">
      {pending.length > 0 && (
        <div className="composer-photos">
          <div className="composer-thumbs">
            {pending.map((p) => (
              <span key={p.id} className="composer-thumb">
                <img src={p.url} alt="" />
                <button type="button" aria-label="Remove photo" onClick={() => setPending((x) => x.filter((y) => y.id !== p.id))}>×</button>
              </span>
            ))}
          </div>
          <div className="composer-purpose" role="radiogroup" aria-label="What these photos are">
            <button type="button" role="radio" aria-checked={purpose === 'work'} className={`chip${purpose === 'work' ? ' is-active' : ''}`} onClick={() => setPurpose('work')}>Her work</button>
            <button type="button" role="radio" aria-checked={purpose === 'sheet'} className={`chip${purpose === 'sheet' ? ' is-active' : ''}`} onClick={() => setPurpose('sheet')}>Filled-in sheets</button>
          </div>
        </div>
      )}
      <div className="composer-inner">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => { addFiles(e.target.files); e.target.value = '' }}
        />
        <button
          type="button"
          className="composer-attach"
          onClick={() => fileRef.current?.click()}
          aria-label="Attach a photo"
          title="Attach a photo of her work or a filled-in sheet"
        >
          <CameraIcon />
        </button>
        <textarea
          ref={ref}
          className="composer-input"
          value={text}
          rows={1}
          placeholder={online ? placeholder ?? 'Ask, tell, or plan…' : 'Offline. Messages send when you reconnect'}
          onChange={(e) => set(e.target.value)}
          onPaste={(e) => { const files = [...e.clipboardData.files]; if (files.length) { e.preventDefault(); addFiles(files) } }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void submit()
            }
          }}
        />
        <button
          className="composer-send"
          onClick={() => void submit()}
          disabled={(!text.trim() && !pending.length) || busy}
          aria-label={busy ? 'Uploading' : 'Send'}
        >
          {busy ? <span className="composer-spin" /> : <ArrowUpIcon />}
        </button>
      </div>
    </div>
  )
}

const ArrowUpIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
)
const CameraIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
)
